import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { Organization } from "../../../../core/models/entities/Organization";
import { DesignChangeStyleForm_VM } from "../components/design-change-style-form.component";
import { useTranslation } from "react-i18next";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useDesignActionHandler } from "../../design-list/hooks/useDesignActionHandler";
import { useState } from "react";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { DesignChangeStyleForm } from "../models/DesignChangeStyleForm";
import { Style } from "../../../../core/models/entities/Style";
import { toast } from "react-toastify";
import { Role } from "../../../../core/models/entities/Role";
import { isAllowed } from "../../../shared/functions/isAllowed";
import { Design } from "../../../../core/models/entities/Design";

export function useDesignStyleUpdateFormViewModel(
  manufacturers: Manufacturer[],
  design: Design
): DesignChangeStyleForm_VM {
  const { t } = useTranslation();
  const { profile, role } = useProfileContext();
  const { styleRepository } = useRepositoriesContext();
  const {
    handleChangeStyle,
    isLoading,
    handleGoToUpdateDesign,
    handleGoToDesignList,
  } = useDesignActionHandler();
  const [pageQuery, setPageQuery] = useState<PageQuery>({
    page: { index: 1, size: 50 },
  } as PageQuery);
  const userRole = profile?.userOrganizationInformations?.role!;
  const [selectedVendor, setSelectedVendor] = useState<Organization | null>(
    design.vendor || null
  );
  const [form, setForm] = useState<DesignChangeStyleForm>({
    organization: design.customer,
    style: design.style,
  } as DesignChangeStyleForm);

  const {
    data,
    fetchNextPage,
    isLoading: isStylesLoading,
  } = styleRepository.useAllStyleAsInfinityQuery(pageQuery);
  let styles: Style[] = [];
  if (data) {
    if (!form.style) {
      setForm((f) => ({ ...f, style: data.pages[0].results[0] }));
    }
    for (const datum of data.pages) {
      styles = styles.concat(datum.results);
    }

    // Sort styles to prioritize exact matches when there's a search term
    if (pageQuery.searchTerm && pageQuery.searchTerm.trim() !== "") {
      const searchTerm = pageQuery.searchTerm.toLowerCase().trim();
      styles = styles.sort((a, b) => {
        // Calculate match scores for style a
        const aExactModel = a.model.toLowerCase() === searchTerm ? 100 : 0;
        const aExactName = a.name.toLowerCase() === searchTerm ? 100 : 0;
        const aExactModelAndName =
          a.modelAndName.toLowerCase() === searchTerm ? 100 : 0;
        const aStartsWithModel = a.model.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const aStartsWithName = a.name.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const aStartsWithModelAndName = a.modelAndName
          .toLowerCase()
          .startsWith(searchTerm)
          ? 50
          : 0;
        const aContainsModel = a.model.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const aContainsName = a.name.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const aContainsModelAndName = a.modelAndName
          .toLowerCase()
          .includes(searchTerm)
          ? 10
          : 0;

        const aScore = Math.max(
          aExactModel,
          aExactName,
          aExactModelAndName,
          aStartsWithModel,
          aStartsWithName,
          aStartsWithModelAndName,
          aContainsModel,
          aContainsName,
          aContainsModelAndName
        );

        // Calculate match scores for style b
        const bExactModel = b.model.toLowerCase() === searchTerm ? 100 : 0;
        const bExactName = b.name.toLowerCase() === searchTerm ? 100 : 0;
        const bExactModelAndName =
          b.modelAndName.toLowerCase() === searchTerm ? 100 : 0;
        const bStartsWithModel = b.model.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const bStartsWithName = b.name.toLowerCase().startsWith(searchTerm)
          ? 50
          : 0;
        const bStartsWithModelAndName = b.modelAndName
          .toLowerCase()
          .startsWith(searchTerm)
          ? 50
          : 0;
        const bContainsModel = b.model.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const bContainsName = b.name.toLowerCase().includes(searchTerm)
          ? 10
          : 0;
        const bContainsModelAndName = b.modelAndName
          .toLowerCase()
          .includes(searchTerm)
          ? 10
          : 0;

        const bScore = Math.max(
          bExactModel,
          bExactName,
          bExactModelAndName,
          bStartsWithModel,
          bStartsWithName,
          bStartsWithModelAndName,
          bContainsModel,
          bContainsName,
          bContainsModelAndName
        );

        // Sort by score (higher scores first), then by model name alphabetically
        if (bScore !== aScore) {
          return bScore - aScore;
        }
        return a.modelAndName.localeCompare(b.modelAndName);
      });
    }
  }

  function handleNexPage() {
    fetchNextPage().then();
  }

  function handleFilterOnManufacturer(manufacturer: Manufacturer | null) {
    setPageQuery((x) => ({
      ...x,
      filterBy: manufacturer
        ? [
            {
              field: "manufacturer.id",
              value: "" + manufacturer.id,
            },
          ]
        : [],
    }));
  }

  function handleSearchQueryChange(query: string) {
    setPageQuery((x) => ({
      ...x,
      searchTerm: query,
    }));
  }

  function handleSelectCustomer(customer: Organization) {
    setForm((f) => ({ ...f, organization: customer }));
  }

  function onSelectVendor(vendor: Organization | null) {
    setSelectedVendor(vendor);
  }

  function handleSelectStyle(style: Style) {
    setForm((f) => ({ ...f, style: style }));
  }

  function handleChangeTitle(title: string) {
    setForm((x) => ({ ...x, title: title }));
  }

  function handleSubmitForm() {
    if (!form) {
      return;
    }

    if (!form.style) {
      toast.warn(t("toasts.no_styles_selected"));
      return;
    }

    handleChangeStyle(design.customerId, design.id, form.style.id)
      .then((updatedDesign) => {
        handleGoToUpdateDesign(updatedDesign);
      })
      .catch(() => {
        // withToasts (in handleChangeStyle) already shows the error toast;
        // this catch only stops navigation and the unhandled rejection.
      });
  }

  function handleCancel() {
    handleGoToDesignList();
  }

  return {
    translate: t,
    styles,
    isStylesLoading,
    onLoadMoreStyles: handleNexPage,
    manufacturers: manufacturers,
    onFilterByManufacturer: handleFilterOnManufacturer,
    searchQuery: pageQuery.searchTerm ?? "",
    onSearchQueryChange: handleSearchQueryChange,
    selectedStyle: form.style,
    onSelectStyle: handleSelectStyle,
    showCustomerInfo: isAllowed(role!, Role.Vendor),
    organization: design.customer,
    title: design.title,
    selectedCustomer: form.organization!,
    onChangeTitle: handleChangeTitle,
    onSelectCustomer: handleSelectCustomer,
    selectedVendor: selectedVendor,
    onSelectVendor: onSelectVendor,
    onSubmit: handleSubmitForm,
    disableSubmit: !validateForm(form),
    onCancel: handleCancel,
    isLoading: isLoading,
    role: userRole,
  };
}

function validateForm(form: DesignChangeStyleForm): boolean {
  return !!form.style;
}
